import React from 'react';
import { Box, Container, Typography, Paper, Divider } from '@mui/material';

const TermsPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
          Terms and Conditions
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            Summary: Read this first!
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                We do not provide investment advice. We provide analytics software. Always seek the advice of a registered financial professional before making investment decisions based on the provided software recommendations.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                We cannot guarantee our software is error-free and that analytics will always be 100% accurate at all times.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                Our content is intended for personal use. If you are an institution or another professional service, you need to contact support@seattlemarketanalytics.com to obtain a license.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                Do not share your account information with others. Our content cannot be redistributed under any circumstances without our explicit permission.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                We respect your privacy. We will never sell your information to any 3rd party.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                Membership is a privilege, not a right. We reserve the right to cancel your membership for any reason.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                We reserve the right to update all disclaimers, terms, and conditions at any time without notice.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            1. Terms of Use
          </Typography>
          <Typography paragraph>
            These terms of use together with the policies identified in Section 2 below (collectively referred to as the "Terms of Use") constitute a legally binding agreement between you and us and serve to define the terms on which you may make use of our website <strong>www.seattlemarketanlytics.ai</strong> (our "Site"), and any other products or services of Seattle Market Analytics LLC. Use of our Site includes accessing, browsing, or registering to use our Site or affiliate social media pages. The information on our Site and affiliated social media pages is for personal, non-commercial use only. By using our Site, or any other products or services of Seattle Market Analytics, you are indicating that you have read and accepted these Terms of Use and the documents referred to below. If you do not agree to these Terms of Use you may not use our software.
          </Typography>
          <Typography paragraph>
            If you subscribe to our service through a corporate other group membership, the words "you" or "your" in these Terms of Use policy you must contact the owners of Seattle Market Analytics LLC owners in writing requesting institutional pricing.
          </Typography>
          <Typography>
            Please read these Terms of Use carefully before you start to use our Site, and we recommend that you print a copy of this for future reference.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            2. Other Applicable Terms
          </Typography>
          <Typography paragraph>
            These Terms of Use refer to the following additional terms, which also apply to your use of our Site:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                Our Privacy Policy, which sets out the terms on which we process any personal data we collect from you, or that you provide to us. By using our Site, you consent to such processing and you warrant that all data provided by you is accurate.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                Our Acceptable Use Policy, which sets out the permitted uses and prohibited uses of our Site. When using our Site, you must comply with this Acceptable Use Policy.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            3. Important Securities Disclaimer
          </Typography>
          <Typography paragraph>
            Seattle Market Analytics LLC is not a financial advisor, professional investment firm, all software recommendations or owner claims of buying or selling securities should be discussed with your own financial advisor for due diligence purposes. We are an AI driven software analytics provider for financial securities research, the Site, the social media pages, the Site Content, and the information therein does not include, nor shall it be construed as including, advice, guidance or recommendations to take, or not to take, any actions or decisions in relation to any matter, including in relation to investments or the purchase or sale of any securities, shares or other assets of any kind. The Information shared is impersonal and not adapted to any particular client, investor or investment program. You acknowledge that information obtained through the software service is not tailored for you or any specific person and should never be used as a substitute for financial or other professional advice. You should not treat any opinions or analytics on the Site or in the Site Content as a recommendation to make any particular investment without professional due diligence support. Should you take any such action or decision based on information on affiliated Seattle Market Analytics related pages, you do so entirely at your own risk. Seattle Market Analytics LLC, its affiliates and their respective representatives shall not be responsible or liable for the accuracy, usefulness or availability of any information transmitted or made available via any affiliated Seattle Market Analytics content page, and shall not be responsible or liable for any trading or investment decisions made based on such information.
          </Typography>
          <Typography paragraph>
            Past performance is not indicative of future results. Investments and strategies described on the by Seattle Market Analytics LLC are speculative and involve significant risk. There is no guarantee that any strategy, investment or investment model portfolio discussed will be successful and actual results may differ substantially from any projections. Every educational investment idea has the potential for loss as well as profit.
          </Typography>
          <Typography>
            Seattle Market Analytics Content constitutes "forward-looking statements" which can be identified by the use of forward-looking terminology such as "may," "will," "should," "expect," "anticipate," "project," "continue" or "believe" or the negatives thereof or other variations thereon or comparable terminology. Any projections, market outlooks or estimates on the Site are forward-looking statements and are based upon certain assumptions. Due to various risks and uncertainties, actual market events, opportunities or results or strategies may differ materially from those reflected in or contemplated by such forward-looking statements and any such projections, outlooks or assumptions should not be construed to be indicative of the actual events that will occur.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            4. Registration, Account, and Password
          </Typography>
          <Typography paragraph>
            In order to access certain Site Content and other products and services of Seattle Market Analytics LLC, you will have to create an account and become a member. Membership is offered on a monthly or annual basis from the date of subscription and will be automatically renewed at the expiry of the initial term (unless you cancel your membership prior to the automatic renewal taking place). If you are not a member you may browse all areas of the Site or use the parts of the Services that are not limited to Members only. To become a subscribing member you must provide a valid payment method and other payment information at the time of registration. Please refer to Fees, Automatic Renewal and Termination below for further billing information. When you register, you will be asked to provide a username, password and/or other piece of information as part of our security procedures. You must treat such information as confidential. In particular, you are responsible for the privacy and safety of your password and you agree not to disclose it to a third party. By becoming a member (i) you agree to immediately notify us of any unauthorized use of your password, (ii) you warrant that all information provided by you (whether at time of registration or afterwards) is accurate, true and complete at the time it is given; (iii) you consent to the use of electronic means to provide you with any notices given pursuant to these Terms of Use, and (iv) you consent to the use of electronic records to store information related to our Site or Terms of Use.
          </Typography>
          <Typography paragraph>
            We have the right to disable any user identification code or password, whether chosen by you or allocated by us, at any time, if in our reasonable opinion you have failed to comply with any of the provisions of these Terms of Use. If you know or suspect that anyone other than you knows your user identification code or password, you must promptly notify us at support@seattlemarketanalytics.com.
          </Typography>
          <Typography>
            Your access to membership is a privilege, and not a right. We have the right to block/deny/restrict the usage of the same by you at any time for any reason, including without limitation, any misuse, use in any manner inconsistent with this Agreement or inappropriate conduct, including conduct which (i) is defamatory, obscene, pornographic, vulgar or offensive; (ii) promotes discrimination, bigotry, racism, hatred, harassment or harm against any individual or group; (iii) is violent or threatening or promotes violence or actions that are threatening to any person or entity; (iv) promotes illegal or harmful activities or substances, or encourages any conduct that would violate, any applicable law or regulation or (v) misappropriates or violates a third party's any rights, or rights of publicity or privacy.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            5. Fees, Automatic Renewal, and Termination
          </Typography>
          <Typography paragraph>
            If you are a paid member, your membership will be automatically renewed and billing will occur monthly or annually based on your membership plan.
          </Typography>
          <Typography paragraph>
            To verify a new subscriber's credit card details when entering a trial, a $1 authorization transaction may appear on your bank statement. A corresponding $1 refund is automatically processed in these instances, but it may take a few days to appear on your bank statement.
          </Typography>
          <Typography paragraph>
            We reserve the right to adjust the price of membership at any time. By purchasing a membership, you authorize us to charge the applicable fee amount at the time of purchase or activation (at our discretion) and annually thereafter.
          </Typography>
          <Typography paragraph>
            We use a third-party company to perform membership payment functions on our behalf. In the event that we are informed by the third-party payment company that your membership fee has not been paid in full, you may be contacted by our third-party payment company to rectify the payment issue and be asked to provide an alternative method of payment. In the event of a default in payment for any reason, we reserve the right to cancel your membership at any time thereafter or to continue your membership until the payment default is resolved.
          </Typography>
          <Typography paragraph>
            You may cancel your membership at any time by contacting us using the details below under the heading "Contact us." We reserve the right to cancel your membership in the event of non-compliance with any of these Terms of Use.
          </Typography>
          <Typography>
            You will not be entitled to a refund of the annual membership fee in the event that your membership is terminated partway through the year.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            6. Changes to these Terms
          </Typography>
          <Typography paragraph>
            We may modify any of these Terms of Use at any time by amending this page and we may at any time amend any of our policies referred to in these terms under Other Applicable Terms. Such amended Terms of Use and any amended policies shall govern our relationship. By continuing to access or use our Site after such modification of these terms, you agree to be bound by the modified Terms of Use. If any modified Terms of Use or modified policies are not acceptable to you, your sole remedy is to cease using our Site.
          </Typography>
          <Typography>
            Please check this page from time to time to take notice of any changes we made, as they are binding on you. This page was last updated on May 17, 2024.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            7. Changes to our Site
          </Typography>
          <Typography paragraph>
            We may update our Site from time to time, and may change the content at any time. However, please note that any of the content on our Site may be out of date at any given time, and we are under no obligation to update it.
          </Typography>
          <Typography>
            We do not guarantee that our Site, or any content on it, will be free from errors or omissions.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            8. Accessing our Site
          </Typography>
          <Typography paragraph>
            We do not guarantee that our Site, or any content on it, will always be available or be uninterrupted. We may suspend, withdraw, discontinue or change all or any part of our Site without notice. We will not be liable to you if for any reason our Site is unavailable at any time or for any period.
          </Typography>
          <Typography paragraph>
            You are responsible for making all arrangements necessary for you to have access to our Site.
          </Typography>
          <Typography>
            You are also responsible for ensuring that all persons who access our Site through your internet connection are aware of these Terms of Use and other applicable terms and conditions, and that they comply with all of them.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            9. Intellectual Property Rights
          </Typography>
          <Typography paragraph>
            We are the owner or the licensee of, or otherwise have permission to use, all intellectual property rights on our Site and the material published on it. Those works are protected by copyright laws and treaties around the world. All such rights are reserved.
          </Typography>
          <Typography paragraph>
            If any pages are permitted to be downloaded from our Site, you agree to not modify the paper or digital copies of any materials you have printed off or downloaded in any way, and you agree not use any illustrations, photographs, video or audio sequences or any graphics separately from any accompanying text.
          </Typography>
          <Typography paragraph>
            Our status (and that of any identified contributors) as the software or authors of content on our Site must always be acknowledged.
          </Typography>
          <Typography paragraph>
            You must not use any part of the content on our Site for commercial purposes without obtaining a license to do so from us.
          </Typography>
          <Typography paragraph>
            If you print off, copy or download any part of our Site in breach of these Terms of Use, your right to use our Site will cease immediately and you must, at our option, return or destroy any copies of the materials you have made.
          </Typography>
          <Typography>
            You must instruct all persons who access our Site through your internet connection to comply with these rules.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            10. Limitation of our Liability
          </Typography>
          <Typography paragraph>
            To the extent permitted by law, we exclude all conditions, warranties, representations or other terms which may apply to our Site or any content on it, whether express or implied.
          </Typography>
          <Typography paragraph>
            We will not be liable to any user for any loss or damage, whether in contract, tort (including negligence), breach of statutory duty, or otherwise, even if foreseeable, arising under or in connection with:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                use of, or inability to use, our Site; or
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                use of or reliance on any content displayed on our Site.
              </Typography>
            </Box>
          </Box>
          <Typography paragraph>
            If you are a consumer, please note that we only provide our Site for domestic and private use. You agree not to use our Site for any commercial or business purposes, and we have no liability to you for any loss of profit, loss of business, business interruption, or loss of business opportunity.
          </Typography>
          <Typography paragraph>
            We will not be liable for any loss or damage caused by a virus, distributed denial-of-service attack, or other technologically harmful material that may infect your computer equipment, computer programs, data or other proprietary material due to your use of our Site or to your downloading of any content on it, or on any website linked to it.
          </Typography>
          <Typography>
            We assume no responsibility for the content of websites linked on our Site. Such links should not be interpreted as endorsement by us of those linked websites. We will not be liable for any loss or damage that may arise from your use of them.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            11. Rights You License
          </Typography>
          <Typography>
            When you upload or post content to our Site, you grant the following licenses to us:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                to modify, electronically reproduce and distribute, and publicly perform and display the content on our Site or affiliate pages.
              </Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>
                to reproduce and distribute through any media now known, or hereafter developed, excerpts of the content in advertisements for, and in marketing and promotional materials related to, our Site or affiliate pages.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            12. Viruses and cyber attacks
          </Typography>
          <Typography paragraph>
            We do not guarantee that our Site will be secure or free from bugs or viruses.
          </Typography>
          <Typography paragraph>
            You are responsible for configuring your information technology, computer programmes and platform in order to access our Site. You should use your own virus protection software.
          </Typography>
          <Typography>
            You must not misuse our Site by knowingly introducing viruses, trojans, worms, logic bombs or other material which is malicious or technologically harmful. You must not attempt to gain unauthorized access to our Site, the server on which our Site is stored or any server, computer or database connected to our Site. You must not attack our Site via a denial-of-service attack or a distributed denial-of service attack. By breaching this provision, you may commit a criminal offence. We will report any such breach to relevant law enforcement authorities and we will co-operate with those authorities by disclosing your identity to them. In the event of such a breach, your right to use our Site will cease immediately.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            13. Linking to our content
          </Typography>
          <Typography paragraph>
            You may link to our home page, provided you do so in a way that is fair and legal and does not damage our reputation or take advantage of it.
          </Typography>
          <Typography paragraph>
            You must not establish a link in such a way as to suggest any form of association, approval or endorsement on our part where none exists.
          </Typography>
          <Typography paragraph>
            You must not establish a link to our Site in any website that is not owned by you.
          </Typography>
          <Typography paragraph>
            Our Site must not be framed on any other Site, nor may you create a link to any part of our Site other than the home page.
          </Typography>
          <Typography paragraph>
            We reserve the right to withdraw linking permission without notice.
          </Typography>
          <Typography paragraph>
            The website in which you are linking must comply in all respects with the content standards set out in our Acceptable Use Policy.
          </Typography>
          <Typography paragraph>
            If you wish to make any use of content on our Site other than that set out above, please contact support@seattlemarketanalytics.com.
          </Typography>
          <Typography>
            These rules and privileges regarding linking to our Site apply equally to each member of your corporate or other group membership account.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            14. Third-Party Links and Resources in our Site
          </Typography>
          <Typography paragraph>
            Where our Site contains links to other Sites and resources provided by third parties, these links are provided for your information only.
          </Typography>
          <Typography>
            We have no control over the contents of those Sites or resources.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            15. Indemnification
          </Typography>
          <Typography>
            You agree to defend, indemnify, and hold us (and our officers, directors, employees and agents), harmless from and against any claims, liabilities, damages, losses and expenses, including without limitation reasonable attorney's fees and costs, arising from of or in any way connected with (i) access to or use of our Site by you and/or any member of your corporate or group membership or (ii) violation by you and/or any member of your corporate or group membership of these Terms of Use or of any third party right (for example but without limitation, an intellectual property right, publicity, confidentiality, property or privacy right).
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            16. General
          </Typography>
          <Typography paragraph fontWeight="bold">
            For subscribers located in the United States:
          </Typography>
          <Typography paragraph>
            You agree that we have the exclusive legal and marketable rights to our Site's content, which may be protected by copyrights, trademarks, service marks, patents, trade secrets, or other proprietary rights and laws. Nothing in these terms of use can be construed to provide any transfer of a copyright right or other intellectual property right regarding content from us to you. For purposes of this paragraph, a transfer of a copyright right includes, but is not limited to, a license or sale of content from us to you.
          </Typography>
          <Typography paragraph>
            You may only view, print, download, or temporarily store content for personal use and may not sell or alter any content created by us. No license of our content is granted by the use of our Site by you.
          </Typography>
          <Typography paragraph fontWeight="bold">
            Severability and Waiver:
          </Typography>
          <Typography>
            If any provision of these Terms of Use is held to be invalid or unenforceable, the remaining provisions will continue to be of full force and effect. Our election not to enforce (immediately or otherwise) any right or provision of these Terms of Use will not constitute a waiver of such right or provision.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" color="secondary">
            17. Contact Us
          </Typography>
          <Typography paragraph>
            To contact us, please email <strong>support@seattlemarketanalytics.com</strong>.
          </Typography>
          <Typography>
            Thank you for visiting our site.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsPage; 